<template>
  <!-- AXA policies held for a decision are whole schedules, not receipts to settle: a short list
       of their own, with a yes or no per policy. -->
  <CarrierHeldSchedules
    v-if="group === syncSchedule"
    :run="run"
    :embedded="embedded"
    :back-label="backLabel"
    @resolved="$emit('resolved')"
    @close="$emit('close')"
  />

  <BasePreloader v-else-if="loading" color="purple" size="l"/>

  <PaymentsReviewModal
    v-else-if="reviewData"
    :review-data="reviewData"
    :payments-update-type="updateType"
    :file-row-offset="0"
    :allow-chooser="false"
    :description-key="'carriers.import.payments.ReviewDescription'"
    :source-prefix="'carrier'"
    :hide-one-colour-summary="true"
    :embedded="embedded"
    :back-label="backLabel"
    :notice="notice"
    :repreviewing="busy"
    @update="commit"
    @repreview="recheck"
    @exportRows="downloadRows"
    @close="$emit('close')"
  />
</template>

<script>
import BasePreloader from '@/views/RenovaDashboard/components/ui-kit/Buttons/BasePreloader';
import PaymentsReviewModal from '@/views/RenovaDashboard/modules/ExcelFileUploader/PaymentsReviewModal';
import { apiError, receiptsFileName, saveFile } from '@/utils/carrierImport';
import {
  MARK_PAID, CANCEL_POLICY, NO_MATCH, SYNC_SCHEDULE, reviewRowsCsv, runRowResult,
} from '@/utils/carrierPayments';
import CarrierHeldSchedules from './CarrierHeldSchedules';

/** The list has no paging of its own once a group is chosen; this walks it. */
const PAGE = 200;
/** The detail endpoint's own cap. */
const DETAIL_CHUNK = 100;
/**
 * How many rows one resolve request carries.
 *
 * Accepting is not a status flip: marking paid runs a proc per instalment and
 * then rewrites the policy's schedule and its payment rows, and a cancellation
 * also deactivates the policy.
 */
const COMMIT_CHUNK = 25;

/**
 * The carrier's payment proposals, shown in the app's own payments review.
 *
 * Not a screen — an adapter. `PaymentsReviewModal` is the Mark as Paid / Mark
 * as Canceled review reached from Payments, and it is genuinely props-in,
 * events-out: no store, no axios, no route. So a carrier read does not need a
 * second screen that looks like it, only something to put its rows into the
 * shape that one already reads.
 *
 * `paymentsUpdateType` is the whole PAID-versus-CANCELED split, one mode per
 * instance — which is exactly the two entries in the Payments dropdown. The
 * group being reviewed picks it.
 *
 * ## What is deliberately off
 *
 * `allowChooser: false`. The chooser's decisions are SETTLE / SPLIT / CREATE
 * against a particular receipt, and the carrier resolve endpoint understands
 * accept-or-dismiss for a whole staged row. Offering a control the server
 * cannot honour is worse than not offering it. The receipts themselves are
 * still sent, so the AFTER column and the totals are real.
 */
export default {
  name: 'CarrierPaymentsReviewHost',
  components: { BasePreloader, PaymentsReviewModal, CarrierHeldSchedules },
  props: {
    run: { type: Object, required: true },
    /** Any of the three. All of them are read on this screen. */
    group: {
      type: String,
      required: true,
      validator: (v) => [MARK_PAID, CANCEL_POLICY, NO_MATCH, SYNC_SCHEDULE].includes(v),
    },
    /** Passed through: a drawer that opened this renders it in place. */
    embedded: {
      type: Boolean,
      default: false,
    },
    /** What the embedded breadcrumb goes back to, in the host's own words. */
    backLabel: {
      type: String,
      default: '',
    },
  },
  emits: ['close', 'resolved'],
  data() {
    return {
      reviewData: null,
      loading: true,
      busy: false,
      /**
       * What the last batch did, kept on screen.
       *
       * A toast is gone by the time you look up from the row you were reading,
       * which is when you want to know whether it worked.
       */
      notice: '',
      /** rowNumber -> staged row id, which is what resolve is addressed by. */
      idByRowNumber: {},
      /** rowNumber -> the staged row and its detail, which is what a section's download is made of. */
      stagedByRowNumber: {},
    };
  },
  computed: {
    syncSchedule() {
      return SYNC_SCHEDULE;
    },
    updateType() {
      return this.group === CANCEL_POLICY ? 'CANCELED' : 'PAID';
    },
    /**
     * Lines the reconcile could not place against any policy.
     *
     * Nothing here can be applied — that is the whole reason they are here —
     * so they go to the review's own "unable to apply" section, which exists
     * for exactly this and prints a WHY column beside each one.
     */
    unplaceable() {
      return this.group === NO_MATCH;
    },
  },
  created() {
    // The held list loads its own rows.
    if (this.group !== SYNC_SCHEDULE) this.load();
  },
  methods: {
    /**
     * A section's rows as a CSV, made here from what was loaded: the review screen only
     * names the rows, and a carrier read has no uploaded file to cut them from.
     */
    downloadRows({ exportRows, sectionKey } = {}) {
      const staged = (exportRows || []).map((n) => this.stagedByRowNumber[n]).filter(Boolean);
      const blocked = new Map(staged.map((e) => [e.row, e.detail.blocked]));
      const csv = reviewRowsCsv(
        staged.map((e) => e.row),
        (row) => blocked.get(row) || this.$t(runRowResult(row).label)
      );
      // noPolicy -> handle-no-policy-2026-10-16.csv
      const what = String(sectionKey || 'rows').replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
      saveFile(
        new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }),
        receiptsFileName(this.run.jobType, new Date(), what)
      );
    },
    /**
     * The whole group, because the review screen has no paging: `reviewData`
     * carries every row and counts them itself.
     */
    fetchRows(page = 0, acc = []) {
      return this.$store.dispatch('carrierImportFetchPaymentRows', {
        scrapeJobId: this.run.scrapeJobId,
        reviewStatuses: ['PENDING'],
        groups: [this.group],
        limit: PAGE,
        page,
      }).then((data) => {
        const items = (data && data.items) || [];
        const rows = acc.concat(items);
        const total = (data && data.totalCount) || rows.length;
        if (items.length === PAGE && rows.length < total) return this.fetchRows(page + 1, rows);
        return rows;
      });
    },
    fetchDetails(rows, index = 0, acc = {}) {
      const slice = rows.slice(index, index + DETAIL_CHUNK);
      if (!slice.length) return Promise.resolve(acc);
      return this.$store.dispatch('carrierImportFetchPaymentDetails', slice.map((r) => r.id))
        .then((items) => {
          (items || []).forEach((d) => { acc[d.id] = d; });
          return this.fetchDetails(rows, index + DETAIL_CHUNK, acc);
        });
    },
    load() {
      this.loading = true;
      return this.fetchRows()
        .then((rows) => this.fetchDetails(rows).then((details) => {
          this.reviewData = this.buildReviewData(rows, details);
        }))
        .catch((err) => {
          this.$notify.error({ message: apiError(err, this.$t('adminTitles.someError')) });
          // Nothing to show and no modal to close: without this the page sits
          // on a group that never opens.
          this.$emit('close');
        })
        .finally(() => { this.loading = false; });
    },
    /**
     * One staged row becomes one review row.
     *
     * `rowNumber` is the report's own line, already an integer and unique
     * within a run, which is all the review asks of it — it keys every decision
     * and is what comes back on commit. `fileRowOffset: 0` stops it being
     * renumbered as a spreadsheet line.
     */
    buildReviewData(rows, details) {
      const idByRowNumber = {};
      const staged = {};
      const schedules = {};
      const complete = [];
      const partial = [];
      const unplaced = [];

      rows.forEach((row) => {
        if (!Number.isInteger(row.rowNumber)) return;
        idByRowNumber[row.rowNumber] = row.id;
        const d = details[row.id] || {};
        staged[row.rowNumber] = { row, detail: d };
        const carrier = row.detail || {};
        const next = (d.instalments || [])[0] || {};

        if (d.matchedPolicyId && (d.instalments || []).length) {
          schedules[d.matchedPolicyId] = {
            currencyCode: d.currencyCode,
            // The shape the receipts table already renders. UNPAID is what the
            // chooser would consider pickable; it is off, but the status still
            // tells the reader which of these are still open.
            receipts: (d.instalments || []).map((i) => ({
              id: i.id,
              dueDate: i.dueDate,
              paidDate: i.paidDate,
              amount: Number(i.amount),
              status: i.status,
            })),
          };
        }

        const reviewRow = {
          rowNumber: row.rowNumber,
          // A row the server would refuse carries no policy id on purpose. That
          // is what makes the review treat it as one it cannot act on: no
          // checkbox, no decision, never committed -- rather than a selectable
          // row that only fails at the end. Its number still shows, below.
          // No policy id on a row nothing can be done with, which is what puts
          // it in the unable section: no checkbox, no decision, never
          // committed. True of every unplaceable line by definition, and of a
          // row the server would otherwise refuse at the end.
          policyId: (this.unplaceable || d.blocked) ? null : (d.matchedPolicyId || null),
          // And the sentence that says why, which the server already wrote. Dropping it left a
          // row with no checkbox and nothing to explain the absence -- the review can only
          // otherwise offer its own generic reason, which does not know about receipts issued.
          blockedNote: d.blocked || '',
          policyNumber: d.policyNumber || row.policyNumber,
          csvPolicyNumber: row.policyNumber,
          fullName: d.holderName || '',
          csvFullName: row.clientName || '',
          // What Renova holds against what the carrier says, which is what the
          // IN RENOVA and IN FILE columns are for.
          paymentAmount: next.amount !== undefined ? Number(next.amount) : null,
          paymentDate: next.dueDate || null,
          csvPaymentAmount: carrier.montoProximoPago !== undefined
            ? Number(carrier.montoProximoPago) : null,
          csvPaymentDate: carrier.ultimaFechaPago || null,
          installmentId: next.id || null,
          suggestedInstallmentId: next.id || null,
          // Named candidates, so a row the matcher could not decide shows the
          // policies it was choosing between rather than a bare warning.
          candidates: (d.candidates || []).map((c) => ({
            policyId: c.insurancePolicyId,
            policyNumber: c.policyNumber,
            fullName: c.holderName,
          })),
          candidateCount: (d.candidates || []).length,
          // The flag the review trusts over any reason code, and the one the
          // commit is computed from.
          willApply: !this.unplaceable && !d.blocked && Boolean(d.matchedPolicyId),
          mismatch: this.mismatchFor(row, d),
        };
        if (this.unplaceable) unplaced.push(reviewRow);
        else (reviewRow.mismatch ? partial : complete).push(reviewRow);
      });

      this.idByRowNumber = idByRowNumber;
      this.stagedByRowNumber = staged;
      return {
        completeMatches: complete,
        partialMatches: partial,
        noMatches: unplaced,
        // One number matching several policies is reported per row through
        // `candidates`, not as a bucket of its own -- sending MULTIPLE_POLICIES
        // would put a "Check again" button on a screen with nothing to re-read.
        multipleMatches: [],
        schedules,
        totalRows: complete.length + partial.length + unplaced.length,
      };
    },
    /**
     * Why a row cannot simply be settled, in the review's own vocabulary.
     *
     * An unrecognised code is handled: the review places it by `willApply` into
     * "other" or "unable to apply" and prints its own unknown-reason line, so a
     * carrier-specific reason is safe. Three codes are avoided deliberately —
     * NAME_DOES_NOT_MATCH, SAME_RECEIPT_AS_ANOTHER_ROW and, on a cancel run,
     * POLICY_HAS_NO_SCHEDULE — because each makes the review seed a decision
     * and force a re-check the moment it opens.
     */
    mismatchFor(row, detail) {
      // Not null. `null` is this screen's code for "policy, date, amount and
      // insured all matched exactly", which files a row under "Everything
      // matches · there is nothing to decide" -- and nothing here was compared
      // to a receipt at all. The carrier named a date; accepting settles every
      // receipt up to it. That is a proposal, and it says so.
      if (this.unplaceable || !detail.matchedPolicyId) return 'NO_POLICY';
      return 'CARRIER_PROPOSAL';
    },
    /**
     * The review will not commit until its rows come back agreeing with the
     * decisions it just sent, so this hands them back.
     *
     * Recomputed here rather than server-side because the only decision this
     * screen can express is include-or-exclude, and that needs nothing the
     * client does not already know. **The moment `allowChooser` is turned on
     * this has to become a real round-trip** — settle-this-receipt, split and
     * create all change what would be written, and only the server can say what.
     */
    recheck({ rowDecisions }) {
      const skipped = new Set(
        (rowDecisions || []).filter((d) => d.action === 'SKIP').map((d) => d.rowNumber),
      );
      const apply = (rows) => rows.map((r) => ({
        ...r,
        willApply: r.willApply && !skipped.has(r.rowNumber),
      }));
      // A new object, or the review's watcher does not fire and Apply stays dead.
      this.reviewData = {
        ...this.reviewData,
        completeMatches: apply(this.reviewData.completeMatches),
        partialMatches: apply(this.reviewData.partialMatches),
      };
    },
    /** Up to three, then "and N more": a list, not a wall. */
    policyNumbersFor(ids) {
      const wanted = new Set(ids);
      const rows = this.reviewData
        ? [].concat(
          this.reviewData.completeMatches,
          this.reviewData.partialMatches,
          this.reviewData.noMatches,
        )
        : [];
      const names = rows
        .filter((r) => wanted.has(this.idByRowNumber[r.rowNumber]))
        .map((r) => r.policyNumber || r.csvPolicyNumber)
        .filter(Boolean);
      if (names.length <= 3) return names;
      return names.slice(0, 3).concat(
        this.$t('carriers.import.payments.AndMore', { count: names.length - 3 }),
      );
    },
    commit({ applyingRowNumbers }) {
      const ids = (applyingRowNumbers || [])
        .map((n) => this.idByRowNumber[n])
        .filter(Boolean);
      if (!ids.length) return Promise.resolve();

      this.busy = true;
      this.notice = '';
      // One key for this press, shared by every chunk of it. The server records
      // the whole press as ONE entry in Payments → Import history, which is
      // what makes it undoable as the one thing the person actually did --
      // rather than six entries because the screen sent it in sixes.
      const idempotencyKey = `${this.run.scrapeJobId}:${this.group}:${Date.now()}`;
      const chunks = [];
      for (let i = 0; i < ids.length; i += COMMIT_CHUNK) {
        chunks.push(ids.slice(i, i + COMMIT_CHUNK));
      }
      let done = 0;
      // Which rows the server refused, so they can be named rather than
      // counted: the endpoint answers per row and that was being collapsed
      // into an integer and thrown away.
      const failedIds = [];

      const step = (index) => {
        if (index >= chunks.length) return Promise.resolve();
        return this.$store.dispatch('carrierImportResolvePayments', {
          ids: chunks[index], decision: 'ACCEPT', idempotencyKey,
        }).then((res) => {
          const results = (res && res.results) || [];
          results.forEach((r) => {
            if (r.status === 'FAILED') failedIds.push(r.id);
            else done += 1;
          });
          return step(index + 1);
        });
      };

      return step(0)
        .then(() => {
          const names = this.policyNumbersFor(failedIds);
          this.notice = [
            this.$t('carriers.import.payments.done.ACCEPT', { count: done }),
            names.length
              ? this.$t('carriers.import.payments.CouldNotApply', {
                count: names.length, policies: names.join(', '),
              })
              : '',
          ].filter(Boolean).join(' · ');
          this.$emit('resolved');
          // Deliberately NOT closing. Applying used to throw the person back to
          // the Carriers page at the moment they would want to see what
          // happened. The drawer is behind this now, so closing is theirs to
          // choose and lands them back in the list they came from.
          return this.load();
        })
        .catch((err) => this.$notify.error({
          message: apiError(err, this.$t('adminTitles.someError')),
        }))
        .finally(() => { this.busy = false; });
    },
  },
};
</script>
